import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { identityDocumentVerificationsKeys } from "Api/Queries/Party/useIdentityDocumentVerificationsQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { VERIFICATIONS_QUERY_KEY } from "Api/Queries/Verifications/useVerificationsQuery";
import {
  type DocumentCheckResultDto,
  type PostPartiesPublicIDUploadDocumentsFetchResponse,
  UploadIdentificationCommandResultStatus2,
  apiPost,
  getApiUrl,
  postPartiesPublicIDUploadDocumentsPath,
} from "Infrastructure/Api/Api";
import type { SecondDocumentType } from "Components/Onboarding/shared/DocumentsUpload/types";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

export type UploadedDocuments = {
  firstIDCardFrontSide: File;
  firstIDCardBackSide: File;
  secondIDCardFrontSide?: File;
  secondIDCardBackSide?: File;
  secondDocumentType?: SecondDocumentType;
};

export type UploadIdentityDocumentsVariables = {
  documents: UploadedDocuments;
  partyPublicID: string;
};

export type DocumentsUploadResult = {
  documentChecks: Array<DocumentCheckResultDto>;
  /** The automated check declined a document; the upload itself succeeded. */
  requiresManualReview: boolean;
  /** Failed automated checks the person may still make. */
  verificationAttemptsLeft: number;
};

type Options = Pick<
  UseMutationOptions<
    DocumentsUploadResult,
    unknown,
    UploadIdentityDocumentsVariables
  >,
  "onSuccess" | "onSettled"
> & {
  /** Toasted after the upload; a declined document gets a neutral warning instead. Omit when the caller presents the result itself. */
  successMessage?: string;
};

const ApiResources = Resources.ApiStatuses;

export const useUploadIdentityDocuments = ({
  successMessage,
  ...options
}: Options = {}) => {
  const { t } = useResource();
  const queryClient = useQueryClient();

  // Awaited before the caller's callbacks, so they see the refreshed party.
  const invalidate = (partyPublicID: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(partyPublicID),
      }),
      // Prefix only: useVerificationsQuery registers [VERIFICATIONS] without a user id.
      queryClient.invalidateQueries({ queryKey: [VERIFICATIONS_QUERY_KEY] }),
      queryClient.invalidateQueries({
        queryKey: identityDocumentVerificationsKeys.byPublicId(partyPublicID),
      }),
    ]);

  return useMutation({
    ...options,
    mutationFn: async ({
      documents,
      partyPublicID,
    }: UploadIdentityDocumentsVariables): Promise<DocumentsUploadResult> => {
      const form = new FormData();

      form.append("FirstIDCardFrontSide", documents.firstIDCardFrontSide);
      form.append("FirstIDCardBackSide", documents.firstIDCardBackSide);

      if (documents.secondIDCardFrontSide) {
        form.append("SecondIDCardFrontSide", documents.secondIDCardFrontSide);
      }

      if (documents.secondIDCardBackSide) {
        form.append("SecondIDCardBackSide", documents.secondIDCardBackSide);
      }

      if (documents.secondDocumentType) {
        form.append("SecondDocumentType", documents.secondDocumentType);
      }

      const result = await (apiPost(
        `${getApiUrl()}${postPartiesPublicIDUploadDocumentsPath(partyPublicID)}`,
        form,
        { headers: new Headers() },
      ) as Promise<PostPartiesPublicIDUploadDocumentsFetchResponse>);

      // A rejected upload (unreadable document, missing side, foreign party) comes back as 4xx
      // problem details. Throwing the response keeps it a breadcrumb in Sentry and reserves
      // events for real 5xx failures; the generic Error below is for a 200 with a non-Success status.
      if (result.status >= 400 || result.status === 0) {
        throw result;
      }

      if (
        result.data?.status !== UploadIdentificationCommandResultStatus2.Success
      ) {
        throw new Error(
          result.data?.status
            ? t(
                ApiResources.UploadIdentificationCommandResultStatus2[
                  result.data?.status
                ],
              )
            : t(ApiResources.UnknownError),
          { cause: { result } },
        );
      }

      const { documentChecks } = result.data;

      return {
        documentChecks,
        requiresManualReview: documentChecks.some(
          (check) => check.requiresManualReview,
        ),
        verificationAttemptsLeft: result.data.verificationAttemptsLeft,
      };
    },
    onSuccess: async (result, variables, ...rest) => {
      await invalidate(variables.partyPublicID);
      if (successMessage) {
        if (result.requiresManualReview) {
          toast.warning(t(Resources.Hooks.OCR.Declined));
        } else {
          toast.success(successMessage);
        }
      }
      return options.onSuccess?.(result, variables, ...rest);
    },
    // A non-Success status carries its own translated message (e.g. attempts exhausted); a 4xx does not.
    onError: async (error, { partyPublicID }) => {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t(Resources.Hooks.OCR.Error),
      );
      // A failed upload may still have stored the documents and changed the status.
      await invalidate(partyPublicID);
    },
  });
};
